import { CheckOutlined, CopyOutlined } from '@ant-design/icons'
import { Avatar, Button, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import React, { useMemo, useState, type JSX } from 'react'
import styled from 'styled-components'

import { ACCENT_100, ACCENT_50, AVATAR_HUES, DIVIDER_BG, MUTED_TEXT, MUTED_TEXT_SOFT, RAW_PAYLOAD } from './colors'
import { FilterAction } from './state'
import { colorFor, dateKey, fmtClock, fmtDateDivider, fmtRelative, humanize, monogram, syntaxJson } from './utils'

import { ActivityRow } from 'api/activityLog'

const { Text } = Typography

type DividerRow = {
  __divider: true
  id: string
  label: string
  count: number
}

type TableRow = ActivityRow | DividerRow

function isDivider(row: TableRow): row is DividerRow {
  return (row as DividerRow).__divider === true
}

type Props = {
  rows: ActivityRow[]
  loading: boolean
  dispatch: React.Dispatch<FilterAction>
}

const StyledTableWrap = styled.div`
  .ant-table-tbody > tr > td {
    padding: 6px 10px;
    font-size: 12px;
  }
  .ant-table-thead > tr > th {
    padding: 6px 10px;
    font-size: 11px;
  }
  .ant-table-tbody > tr.activity-row-expanded > td {
    background: ${ACCENT_50};
    border-bottom-color: transparent;
  }
  .ant-table-tbody > tr.activity-row-expanded:hover > td {
    background: ${ACCENT_50};
  }
  .ant-table-tbody > tr.ant-table-expanded-row > td {
    background: ${ACCENT_50};
    border-top: 1px solid ${ACCENT_100};
  }
  .ant-table-tbody > tr.ant-table-expanded-row:hover > td {
    background: ${ACCENT_50};
  }
  .ant-table-tbody > tr.activity-divider-row > td {
    background: ${DIVIDER_BG};
    padding: 4px 10px;
    cursor: default;
  }
  .ant-table-tbody > tr.activity-divider-row:hover > td {
    background: ${DIVIDER_BG};
  }
`

const RawPayload = styled.pre`
  background: ${RAW_PAYLOAD.bg};
  border-radius: 6px;
  color: ${RAW_PAYLOAD.fg};
  font-size: 11px;
  line-height: 1.6;
  margin: 6px 0 0;
  max-height: 280px;
  overflow: auto;
  padding: 10px 12px;
  white-space: pre;

  .k {
    color: ${RAW_PAYLOAD.key};
  }
  .s {
    color: ${RAW_PAYLOAD.string};
  }
  .n {
    color: ${RAW_PAYLOAD.number};
  }
  .b {
    color: ${RAW_PAYLOAD.bool};
  }
`

const DividerLabel = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED_TEXT};
  text-transform: uppercase;
  letter-spacing: 0.06em;

  .count {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 10.5px;
    font-weight: 500;
    color: ${MUTED_TEXT_SOFT};
    text-transform: none;
    letter-spacing: 0;
  }
`

function avatarColor(s: string): string {
  let h = 0
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0
  return AVATAR_HUES[Math.abs(h) % AVATAR_HUES.length]
}

function stop(handler: () => void) {
  return (e: React.MouseEvent) => {
    e.stopPropagation()
    handler()
  }
}

function buildRowsWithDividers(rows: ActivityRow[]): TableRow[] {
  const out: TableRow[] = []
  let currentKey: string | null = null
  let groupStart = 0
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    const k = dateKey(row.occurredAt)
    if (k !== currentKey) {
      const count = rows.slice(i).filter((r) => dateKey(r.occurredAt) === k).length
      out.push({
        __divider: true,
        count,
        id: `__divider-${k}-${groupStart}`,
        label: fmtDateDivider(row.occurredAt),
      })
      currentKey = k
      groupStart = i
    }
    out.push(row)
  }
  return out
}

export function ActivityTable({ rows, loading, dispatch }: Props): JSX.Element {
  const [expandedKeys, setExpandedKeys] = useState<readonly React.Key[]>([])

  const dataSource = useMemo(() => buildRowsWithDividers(rows), [rows])

  const columns: ColumnsType<TableRow> = [
    {
      title: 'Time',
      key: 'time',
      width: 110,
      onCell: (row) => (isDivider(row) ? { colSpan: 5 } : {}),
      render: (_, row) => {
        if (isDivider(row)) {
          return (
            <DividerLabel>
              <span>{row.label}</span>
              <span className="count">
                · {row.count} {row.count === 1 ? 'event' : 'events'}
              </span>
            </DividerLabel>
          )
        }
        return (
          <div style={{ fontFamily: 'monospace', fontSize: 11, lineHeight: 1.35 }}>
            <div>{fmtClock(row.occurredAt)}</div>
            <Text style={{ fontSize: 10 }} type="secondary">
              {fmtRelative(row.occurredAt)}
            </Text>
          </div>
        )
      },
    },
    {
      title: 'Type',
      key: 'type',
      width: 150,
      onCell: (row) => (isDivider(row) ? { colSpan: 0 } : {}),
      render: (_, row) => {
        if (isDivider(row)) return null
        const catKey = row.category?.key || 'event'
        const color = colorFor(catKey)
        const label = row.activityType ? humanize(row.activityType) : row.category?.label || humanize(catKey)
        return (
          <Tag
            onClick={row.category?.key ? stop(() => dispatch({ type: 'toggle_category', key: catKey })) : undefined}
            style={{
              background: color.bg,
              borderColor: color.bg,
              color: color.fg,
              cursor: row.category?.key ? 'pointer' : 'default',
              fontSize: 11,
              margin: 0,
              padding: '0 6px',
            }}
          >
            {label}
          </Tag>
        )
      },
    },
    {
      title: 'Event',
      key: 'event',
      onCell: (row) => (isDivider(row) ? { colSpan: 0 } : {}),
      render: (_, row) => (isDivider(row) ? null : <span style={{ fontSize: 12 }}>{row.event}</span>),
    },
    {
      title: 'Client',
      key: 'client',
      width: 180,
      onCell: (row) => (isDivider(row) ? { colSpan: 0 } : {}),
      render: (_, row) => {
        if (isDivider(row)) return null
        const c = row.client
        if (!c) return <Text type="secondary">—</Text>
        const clickable = !!c.id
        return (
          <div
            onClick={clickable ? stop(() => dispatch({ type: 'toggle_client', id: c.id })) : undefined}
            style={{ cursor: clickable ? 'pointer' : 'default', lineHeight: 1.3 }}
          >
            <div style={{ fontSize: 12, fontWeight: 500 }}>{c.name || '(unnamed)'}</div>
            <Text style={{ fontFamily: 'monospace', fontSize: 10.5 }} type="secondary">
              {c.urlSlug || c.id}
            </Text>
          </div>
        )
      },
    },
    {
      title: 'Actor',
      key: 'actor',
      width: 240,
      onCell: (row) => (isDivider(row) ? { colSpan: 0 } : {}),
      render: (_, row) => {
        if (isDivider(row)) return null
        const a = row.actor
        if (!a)
          return (
            <Text italic style={{ fontSize: 11 }} type="secondary">
              system
            </Text>
          )
        const label = a.email || [a.firstName, a.lastName].filter(Boolean).join(' ') || a.seatId || a.id
        const id = a.seatId || a.id
        const clickable = !!a.seatId
        return (
          <div
            onClick={clickable ? stop(() => dispatch({ type: 'toggle_actor', id })) : undefined}
            style={{
              alignItems: 'center',
              cursor: clickable ? 'pointer' : 'default',
              display: 'flex',
              gap: 6,
              minWidth: 0,
            }}
          >
            <Avatar size={20} style={{ background: avatarColor(label || ''), flexShrink: 0, fontSize: 9 }}>
              {monogram(label || '')}
            </Avatar>
            <Tooltip title={label}>
              <Text ellipsis style={{ flex: 1, fontSize: 11.5, minWidth: 0 }}>
                {label}
              </Text>
            </Tooltip>
          </div>
        )
      },
    },
  ]

  return (
    <StyledTableWrap>
      <Table<TableRow>
        columns={columns}
        dataSource={dataSource}
        expandable={{
          expandedRowKeys: expandedKeys,
          expandedRowRender: (row) => (isDivider(row) ? null : <ExpandedDetails row={row} />),
          expandRowByClick: true,
          onExpandedRowsChange: setExpandedKeys,
          rowExpandable: (row) => !isDivider(row),
          showExpandColumn: false,
        }}
        loading={loading}
        onRow={(row) => (isDivider(row) ? {} : { style: { cursor: 'pointer' } })}
        pagination={false}
        rowClassName={(row) => {
          if (isDivider(row)) return 'activity-divider-row'
          return expandedKeys.includes(row.id) ? 'activity-row-expanded' : ''
        }}
        rowKey={(row) => row.id}
        scroll={{ x: true }}
        size="small"
      />
    </StyledTableWrap>
  )
}

function ExpandedDetails({ row }: { row: ActivityRow }): JSX.Element {
  const src = row.source || {}
  const details = row.details || []
  const [copied, setCopied] = useState(false)

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    const json = JSON.stringify(row, null, 2)
    navigator.clipboard.writeText(json).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  return (
    <div style={{ display: 'grid', gap: 20, gridTemplateColumns: '1fr 1fr', padding: '4px 8px 12px' }}>
      <KvGrid
        items={[
          ...details.map((d) => ({ k: d.label, v: formatDetail(d.value) })),
          ...(row.target ? [{ k: 'target', v: `${row.target.type} · ${row.target.label || row.target.id}` }] : []),
        ]}
      />
      <KvGrid
        items={[
          { k: 'event_id', mono: true, v: src.eventId || '—' },
          { k: 'event_type', mono: true, v: src.eventType || '—' },
          { k: 'bus', v: src.bus || '—' },
          { k: 'service', v: src.service || '—' },
          { k: 'ingested_at', mono: true, v: row.ingestedAt || '—' },
        ]}
      />
      <div style={{ gridColumn: '1 / -1' }}>
        <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
          <Text style={{ fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase' }} type="secondary">
            Raw activity
          </Text>
          <Tooltip title={copied ? 'Copied!' : 'Copy JSON'}>
            <Button
              icon={copied ? <CheckOutlined /> : <CopyOutlined />}
              onClick={handleCopy}
              size="small"
              type="text"
            />
          </Tooltip>
        </div>
        <RawPayload dangerouslySetInnerHTML={{ __html: syntaxJson(row) }} />
      </div>
      <div
        style={{
          borderTop: `1px solid ${ACCENT_100}`,
          color: MUTED_TEXT,
          fontFamily: 'monospace',
          fontSize: 11,
          gridColumn: '1 / -1',
          paddingTop: 10,
          textAlign: 'right',
        }}
      >
        {row.id}
      </div>
    </div>
  )
}

function KvGrid({ items }: { items: { k: string; v: string; mono?: boolean }[] }): JSX.Element {
  return (
    <div style={{ display: 'grid', fontSize: 12, gap: '6px 14px', gridTemplateColumns: 'max-content 1fr' }}>
      {items.map(({ k, v, mono }, i) => (
        <React.Fragment key={`${k}-${i}`}>
          <Text style={{ fontFamily: 'monospace', fontSize: 11 }} type="secondary">
            {k}
          </Text>
          <span style={mono ? { fontFamily: 'monospace', fontSize: 12 } : undefined}>{v}</span>
        </React.Fragment>
      ))}
    </div>
  )
}

function formatDetail(v: unknown): string {
  if (v == null) return '—'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}
